/**
 * Whether a request is bound for our API, and therefore whether the bearer token may be
 * attached to it. Used by the axios request interceptor in plugins/httpRequestServer.ts.
 *
 * IN ITS OWN MODULE ON PURPOSE. The interceptor is registered by a Nuxt plugin, which only
 * runs inside a Nuxt app, so it cannot be exercised by a unit test without dragging in a Nuxt
 * context. This is security-relevant logic that review has already found two defects in, so
 * it needs to be directly testable -- see __tests__/apiRequestUrl.test.ts.
 *
 * DECIDED FROM THE CONFIGURED baseURL, NOT FROM process.env.VUE_APP_API_PREFIX. That env var
 * only exists at build time, and the container image is built with no `.env` file at all so
 * one image can serve every environment (see frontend/Dockerfile). The original
 * `startsWith(process.env.VUE_APP_API_PREFIX ?? "")` collapsed to `startsWith("")` -- true
 * for every URL -- which would have attached the token to every request the instance makes.
 *
 * ORIGIN COMPARISON, NOT A STRING PREFIX. Two ways a prefix check leaks the token:
 *
 *   * scheme-relative URLs. "//cdn.example.com/x" has no scheme, so a `^scheme://` test reads
 *     it as relative and lets the token through to another host entirely.
 *   * suffix tricks. "https://api.example.net.evil.com/" startsWith "https://api.example.net".
 *
 * Resolving with `URL` closes both: it is the same resolution axios performs against baseURL,
 * and comparing `origin` cannot be fooled by a suffix.
 */
export const isApiRequestUrl = (requestUrl: string | undefined, apiBaseUrl: string | undefined): boolean => {
  const url = requestUrl ?? "";
  const base = apiBaseUrl ?? "";

  // No configured base means nothing can be proven to be the API. Fail closed: no token. A pod
  // in this state also fails readiness (server/routes/health/ready.ts), so it should not be
  // serving traffic at all.
  if (base.length === 0) {
    return false;
  }

  let apiOrigin: string;
  try {
    apiOrigin = new URL(base).origin;
  }
  catch {
    // A relative base (e.g. "/api") has no origin to compare against. Fall back to the older
    // meaning: a relative request is same-origin and so ours, an absolute one is not.
    return !/^([a-z][a-z0-9+.-]*:)?\/\//i.test(url);
  }

  try {
    // Relative AND scheme-relative URLs both resolve against the base here, exactly as axios
    // resolves them -- so "//evil.com/x" becomes https://evil.com/x and is correctly judged
    // foreign rather than assumed local.
    return new URL(url, base).origin === apiOrigin;
  }
  catch {
    return false;
  }
};
